import { stringifyToMarketplaceISO } from './dates';
import { getTempListingData } from './localStorage';
import { LISTING_STATE_DRAFT, LISTING_STATE_PENDING_APPROVAL } from './types';
import { createSlug } from './urlHelpers';
import {
    LISTING_PAGE_DRAFT_VARIANT,
    LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../components/EditListingWizard/config';

/**
 * Insert author and author profile image into listing's data
 * might be used for listing card
 * @param {Object} listing sdk listing object
 * @param {Array} included sdk included array
 * @returns sdk listing | null
 */
export const insertAuthorDataIntoListing = (listing, included, skipCheck = false) => {
    const {
        relationships = {},
        attributes: {
            state,
            publicData: { userRepresentationId: isUserListing },
        },
    } = listing;

    const isValidListing = state === 'published';
    if (!isValidListing) {
        return null;
    }

    const { images: relationshipsImage, author: relationshipsAuthor } = relationships || {
        images: { data: [] },
        author: { data: { id: {} } },
    };

    if (isUserListing || skipCheck) {
        const {
            data: {
                id: { uuid: authorId },
            },
        } = relationshipsAuthor;

        const dictionary = (included || []).reduce(
            (acc, item) => {
                const {
                    type,
                    id: { uuid },
                } = item;

                acc[type] = { ...acc[type], [uuid]: item };
                return acc;
            },
            { image: {}, user: {} }
        );
        /**
         * if a listing is a user-listing (rider or horseowner)
         * add author profile image to a listing's data
         */

        const authorDataIncluded = dictionary.user[authorId];
        const authorImageId =
            authorDataIncluded &&
            authorDataIncluded.relationships &&
            authorDataIncluded.relationships.profileImage &&
            authorDataIncluded.relationships.profileImage.data
                ? authorDataIncluded.relationships.profileImage.data.id.uuid
                : undefined;
        const authorImageIncluded = dictionary.image[authorImageId];

        return {
            ...listing,
            author: {
                ...authorDataIncluded,
                profileImage: authorImageIncluded,
            },
        };
    }
    /**
     * listing's images are stored inside included field
     */
    return {
        ...listing,
        images:
            relationshipsImage && relationshipsImage.data
                ? (included || []).filter(
                      ({ type, id: { uuid: imageId } }) =>
                          type === 'image' &&
                          relationshipsImage.data.some(({ id: { uuid } }) => uuid === imageId)
                  )
                : [],
    };
};

/**
 * resolve listing state and get corresp. params & name for navigation
 * @param {object} currentListing
 * @returns params: object, name: string
 */
export const getListingNavParams = currentListing => {
    const {
        id: { uuid: id },
        attributes: { state },
    } = currentListing;

    const slug = createSlug(currentListing);

    const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
    const isDraft = state === LISTING_STATE_DRAFT;
    const variant = isPendingApproval
        ? LISTING_PAGE_PENDING_APPROVAL_VARIANT
        : isDraft
        ? LISTING_PAGE_DRAFT_VARIANT
        : null;

    const params = !!variant
        ? {
              id,
              slug,
              variant,
          }
        : {
              id,
              slug,
          };

    return { params, name: !!variant ? 'ListingPageVariant' : 'ListingPage' };
};
/**
 * Calculates progress taking into account fields filled
 * @returns string
 */
export const calculateProgressForOfflineMode = () => {
    const tabNum = 1;
    const initialProgress = 0;

    const tempListingData = getTempListingData();

    if (!tempListingData || Object.keys(tempListingData).length === 0) {
        return `${tabNum}-${initialProgress}`;
    }

    const calculateValue = value => (!!value ? 1 : 0);

    const {
        horseType,
        startDate,
        availability,
        desiredExperience,
        activities = [],
        desiredDisciplines = [],
        requiredQualificationLevel,
        skillsGeneral = [],
        skillsExperience = [],
    } = tempListingData;

    const progress = [
        { value: horseType },
        { value: startDate },
        { value: availability },
        { value: desiredExperience },
        { value: activities && activities.length },
        { value: desiredDisciplines && desiredDisciplines.length },
        { value: requiredQualificationLevel },
        {
            value:
                (skillsGeneral && skillsGeneral.length) ||
                (skillsExperience && skillsExperience.length),
        },
    ].reduce((acc, { value }) => acc + calculateValue(value), 0);

    return `${tabNum}-${progress}`;
};

/**
 * Combine sdk user data with included profileImage
 * @param {*} user - sdk user data with included field
 * @param {*} state - redux store state
 * @returns sdk user
 */
export const insertProfileImageToUserData = (user, state) => {
    const {
        relationships: { profileImage },
    } = user || { relationships: { profileImage: null } };

    const {
        marketplaceData: {
            entities: { image: imageEntities },
        },
    } = state;

    if (!profileImage || !profileImage.data || !imageEntities[profileImage.data.id.uuid]) {
        return user;
    }

    return {
        ...user,
        profileImage: {
            ...imageEntities[profileImage.data.id.uuid],
        },
    };
};
/**
 * Resolve the date to show as a listing's creation date:
 * approvedAt (set on the first publish) comes first,
 * updatedAt or createdAt are used as a fallback for listings without it
 * @param {object} listing
 * @returns {Date|string|null}
 */
export const getListingDisplayDate = listing => {
    const { createdAt, publicData } = listing?.attributes || {};
    const { approvedAt, updatedAt } = publicData || {};

    return approvedAt || updatedAt || createdAt || null;
};
/**
 * Get Date fields on listing being updated
 */
export const getTimeDataOnListingUpdated = () => {
    return {
        publicData: {
            /**
             * updatedAt is used to notify user/owner
             * that this listing's has been changed
             */
            updatedAt: new Date().toString(),
            /**
             * updatedAtLong is used for search schema, e.g
             * to sort listings by creation/update date
             */
            updatedAtLong: new Date().getTime(),
            /** expiration date for 30, 60, 90 periods */
            lifetimeUpdatedAt: stringifyToMarketplaceISO(new Date()),
        },
        privateData: {
            lifetimeExpired: null,
        },
    };
};
